/**
 * DV-PROJ-001: Projects Page Component
 *
 * Main Projects tab page for managing project-scoped configuration and assets:
 * - Horizontal scrolling projects list
 * - Tabbed interface for Characters/Locations/Assets
 * - Creative Profile dropdown for project defaults
 */

import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Plus,
  Search,
  FolderOpen,
  Users,
  MapPin,
  FileImage,
  Palette,
  AlertCircle,
  ChevronDown
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useProjectsQuery, useCreativeProfilesQuery } from '@/hooks/useProjectsQuery';
import CharacterProfilesSection from '@/components/projects/sections/CharacterProfilesSection';
import SettingProfilesSection from '@/components/projects/sections/SettingProfilesSection';
import AssetsSection from '@/components/projects/sections/AssetsSection';
import CreateProjectModal from '@/components/projects/CreateProjectModal';
import { Skeleton } from '@/components/ui/LoadingSkeletons';
import HolographicCard, { CardHeader, CardContent } from '@/components/futuristic/HolographicCard';
import { formatTimeAgo } from '@/utils/format';
import type { Project } from '@/types/projects';

type ActiveTab = 'characters' | 'locations' | 'assets';

const ProjectsPage: React.FC = () => {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('characters');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreativeDropdown, setShowCreativeDropdown] = useState(false);

  const { data: projectsData, isLoading: projectsLoading, error: projectsError } = useProjectsQuery({
    search: searchQuery || undefined
  });

  const { data: creativeProfilesData } = useCreativeProfilesQuery();

  const projects: Project[] = Array.isArray(projectsData) ? projectsData :
    projectsData?.data?.projects ?? [];
  const selectedProject = selectedProjectId
    ? projects.find(p => p.id === selectedProjectId)
    : null;

  const creativeProfiles = creativeProfilesData?.profiles ?? [];

  // Tab navigation items
  const tabs = [
    {
      id: 'characters' as const,
      name: 'Characters',
      icon: Users,
    },
    {
      id: 'locations' as const,
      name: 'Locations',
      icon: MapPin,
    },
    {
      id: 'assets' as const,
      name: 'Assets',
      icon: FileImage,
    }
  ];

  // Handle project selection
  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  // Handle project creation success
  const handleProjectCreated = (projectId: string) => {
    setSelectedProjectId(projectId);
    setShowCreateModal(false);
  };

  return (
    <div className="min-h-screen bg-black p-6">
      <Helmet>
        <title>Projects - HI-SI Dashboard</title>
        <meta name="description" content="Manage projects, characters, locations, and assets for HI-SI pipeline executions" />
      </Helmet>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-light text-gradient-cyan tracking-wider mb-2">
          PROJECTS
        </h1>
        <p className="text-white/50 font-mono text-sm mb-6">
          Manage characters, locations, assets, and creative profiles for pipeline executions
        </p>

        {/* Search and Create */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 w-4 h-4" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-black/50 border border-cyan-500/20 rounded-lg
                         text-white/90 placeholder-white/40 focus:border-cyan-500/50 focus:outline-none
                         transition-colors font-mono text-sm"
            />
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10 ml-4"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </button>
        </div>
      </div>

      {/* Horizontal Projects Strip */}
      <HolographicCard glow="cyan" intensity="medium" className="mb-8">
        <CardHeader
          title="PROJECTS"
          subtitle={`${projects.length} total`}
          icon={<FolderOpen className="w-5 h-5" />}
        />
        <CardContent className="p-4">
          {projectsLoading ? (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="min-w-[280px] h-24 rounded-lg" />
              ))}
            </div>
          ) : projectsError ? (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-red-400 text-sm">Failed to load projects</p>
            </div>
          ) : projects.length > 0 ? (
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-track-white/5 scrollbar-thumb-cyan-500/30">
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => handleProjectSelect(project.id)}
                  className={cn(
                    'min-w-[280px] p-4 rounded-lg border transition-all duration-300 text-left group',
                    selectedProjectId === project.id
                      ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border-cyan-500/50 text-cyan-400'
                      : 'border-white/10 text-white/70 hover:border-white/20 hover:bg-white/5'
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-mono text-sm font-medium uppercase tracking-wider truncate">
                      {project.name}
                    </h3>
                    <FolderOpen className={cn(
                      'w-4 h-4 transition-colors',
                      selectedProjectId === project.id ? 'text-cyan-400' : 'text-white/40'
                    )} />
                  </div>
                  {project.description && (
                    <p className="text-xs text-white/50 mb-2 line-clamp-2">
                      {project.description}
                    </p>
                  )}
                  <div className="text-xs text-white/30 font-mono">
                    {formatTimeAgo(project.createdAt)}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FolderOpen className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/50 mb-4">No projects yet</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10 text-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Project
              </button>
            </div>
          )}
        </CardContent>
      </HolographicCard>

      {/* Main Content */}
      {selectedProject ? (
        <div className="space-y-6">
          {/* Tabs and Creative Profile Dropdown */}
          <div className="flex items-center justify-between">
            {/* Tabs */}
            <div className="flex bg-black/50 border border-white/10 rounded-lg overflow-hidden">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex items-center gap-2 px-6 py-3 transition-all duration-300',
                      isActive
                        ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-r border-cyan-500/30'
                        : 'text-white/70 hover:text-white/90 hover:bg-white/5 border-r border-white/10 last:border-r-0'
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-mono text-sm font-medium uppercase tracking-wider">
                      {tab.name}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Creative Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowCreativeDropdown(!showCreativeDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-black/50 border border-purple-500/30
                           rounded-lg text-purple-400 hover:bg-purple-500/10 transition-colors"
              >
                <Palette className="w-4 h-4" />
                <span className="font-mono text-sm">Creative Profile</span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {showCreativeDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowCreativeDropdown(false)}
                  />
                  <div className="absolute right-0 top-12 z-20 bg-black/90 backdrop-blur-xl border border-white/20
                                  rounded-lg shadow-xl min-w-[200px]">
                    <div className="p-2">
                      {creativeProfiles.length > 0 ? (
                        creativeProfiles.map((profile) => (
                          <button
                            key={profile.id}
                            onClick={() => {
                              // TODO: Handle creative profile selection
                              console.log('Selected creative profile:', profile);
                              setShowCreativeDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-white/80 hover:bg-white/10
                                       rounded-md transition-colors"
                          >
                            {profile.name}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-white/50">
                          No creative profiles available
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'characters' && (
              <CharacterProfilesSection project={selectedProject} />
            )}
            {activeTab === 'locations' && (
              <SettingProfilesSection project={selectedProject} />
            )}
            {activeTab === 'assets' && (
              <AssetsSection project={selectedProject} />
            )}
          </div>
        </div>
      ) : (
        /* Empty State */
        <HolographicCard glow="blue" intensity="subtle" className="h-full">
          <CardContent className="flex items-center justify-center h-96">
            <div className="text-center">
              <FolderOpen className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-xl font-light text-white/70 mb-2">
                No Project Selected
              </h3>
              <p className="text-white/40 mb-6 max-w-md">
                Select a project from the horizontal list above to manage its characters, locations, and assets.
              </p>
              {projects.length === 0 && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Project
                </button>
              )}
            </div>
          </CardContent>
        </HolographicCard>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleProjectCreated}
      />
    </div>
  );
};

export default ProjectsPage;